import API from '../apiClient';
import { queryEmailLogs } from '../mocks/emailNotifications';

const isMockEnabled = () => {
  const raw = (process.env.REACT_APP_USE_MOCK_EMAIL_LOGS || '').toString().toLowerCase();
  // Only use mocks if explicitly enabled
  return raw === 'true';
};

// params: { status[], contextType[], q, from, to, page, pageSize, organizationId }
export const getEmailLogs = async (params = {}) => {
  if (isMockEnabled()) {
    // Simulate latency
    return new Promise((resolve) => {
      setTimeout(() => {
        const data = queryEmailLogs(params);
        resolve({ data });
      }, 200);
    });
  }

  const { status = [], contextType = [], q, from, to, page = 1, pageSize = 25, organizationId } = params;
  const searchParams = new URLSearchParams();
  status.forEach(s => searchParams.append('status', s));
  contextType.forEach(c => searchParams.append('context_type', c));
  if (q) searchParams.set('q', q);
  if (from) searchParams.set('from', from);
  if (to) searchParams.set('to', to);
  searchParams.set('page', page);
  searchParams.set('page_size', pageSize);
  // Always send organization_id parameter - null/undefined means "all organizations"
  if (organizationId !== undefined) {
    searchParams.set('organization_id', organizationId || '');
  }

  return API.get(`/system/email-logs?${searchParams.toString()}`);
};

export const getTrainingEmailLogs = async (trainingId, params = {}) => {
  if (isMockEnabled()) {
    // Filter mock data by training context
    const data = queryEmailLogs({ ...params, contextType: ['TRAINING'] });
    return { data };
  }
  const { status = [], q, from, to, page = 1, pageSize = 25 } = params;
  const searchParams = new URLSearchParams();
  status.forEach(s => searchParams.append('status', s));
  if (q) searchParams.set('q', q);
  if (from) searchParams.set('from', from);
  if (to) searchParams.set('to', to);
  searchParams.set('page', page);
  searchParams.set('page_size', pageSize);
  return API.get(`/training/${trainingId}/email-logs?${searchParams.toString()}`);
};

export const exportEmailLogsCSV = async (params = {}) => {
  const { status = [], contextType = [], q, from, to, organizationId } = params;
  const searchParams = new URLSearchParams();
  status.forEach(s => searchParams.append('status', s));
  contextType.forEach(c => searchParams.append('context_type', c));
  if (q) searchParams.set('q', q);
  if (from) searchParams.set('from', from);
  if (to) searchParams.set('to', to);
  // Always send organization_id parameter - null/undefined means "all organizations"
  if (organizationId !== undefined) {
    searchParams.set('organization_id', organizationId || '');
  }

  // Use fetch directly to handle blob response
  const token = localStorage.getItem('jwtToken');
  const response = await fetch(`${API.defaults.baseURL}/system/email-logs/export?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export CSV');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `email-notifications-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Get retention settings for an organization
export const getRetentionSettings = async (organizationId) => {
  const searchParams = new URLSearchParams();
  searchParams.set('organization_id', organizationId);
  return API.get(`/system/email-logs/retention-settings?${searchParams.toString()}`);
};

// Update retention settings for an organization
export const updateRetentionSettings = async (settings) => {
  return API.put('/system/email-logs/retention-settings', settings);
};

// Hard delete archived logs before a specific date
export const hardDeleteArchived = async (beforeDate) => {
  return API.post('/system/email-logs/archive/hard-delete', {
    before_date: beforeDate,
  });
};

// Resend an email from an email log
export const resendEmailLog = async (emailLogId) => {
  return API.post(`/system/email-logs/${emailLogId}/resend`);
};

// Queued Email Forecast - recomputed on demand (no persistent queue exists).
// organizationId omitted/undefined -> caller's own org (server-side default).
export const calculateEmailForecast = async ({ organizationId, horizonDays } = {}) => {
  const body = {};
  if (organizationId) body.organization_id = organizationId;
  if (horizonDays) body.horizon_days = horizonDays;
  return API.post('/system/email-forecast/calculate', body);
};

